import { useId } from 'react';
import type { CircleGraphProps } from './CircleGraph.types';

export default function CircleGraph(props: CircleGraphProps) {
  const maskId = useId();

  const {
    className = '',
    size = 54,
    strokeWidth = 4,
    gap = 32,
    progress = 0.5,
    useOrange = false,
    isLabelVisible = true,
    disableAnimation = false,
  } = props;

  // Clamp progress between 0 and 1.
  const clampedProgress = Math.min(1, Math.max(0, progress));
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  // Clamp gap to be higher than 0 and lower than circumference to avoid rendering issues.
  const clampedGap = Math.min(Math.max(0, gap), circumference);
  // Visible part of the circle is the circumference minus the gap.
  const visible = circumference - clampedGap;
  // We want to include strokeWidth because of rounded line caps.
  // It's needed to correctly hide rounded ends of the progress stroke when progress is low.
  const progressLength = (visible + strokeWidth) * clampedProgress;
  const progressGap = circumference - progressLength;
  const strokeDashoffset = visible / 2;
  const progressOffset = strokeDashoffset + strokeWidth;

  // Convert progress to percentage for label display.
  const progressLabel = Math.round(clampedProgress * 100);
  const cx = size / 2;
  const cy = cx;

  const progressClassName = useOrange
    ? 'stroke-pmw-bar-orange-light dark:stroke-pmw-bar-orange-dark'
    : 'stroke-pmw-bar-blue-light dark:stroke-pmw-bar-blue-dark';
  const progressAnimationClassName = disableAnimation
    ? ''
    : 'transition-[stroke-dasharray] duration-500';

  // Helper to render track both for mask and background.
  function renderTrack(className: string) {
    return (
      <circle
        className={className}
        cx={cx}
        cy={cy}
        r={radius}
        fill="none"
        strokeWidth={strokeWidth}
        strokeDasharray={`${visible} ${clampedGap}`}
        strokeDashoffset={strokeDashoffset}
        transform={`rotate(-90 ${cx} ${cy})`}
        strokeLinecap="round"
      />
    );
  }

  return (
    <div className={`relative inline-block ${className}`}>
      {isLabelVisible && (
        <div className="absolute inset-0 flex items-center justify-center font-unibet">
          <span className="text-[25px] text-pmw-label-primary dark:text-pmw-label-primary-dark">
            {progressLabel}
          </span>
          <span className="text-[15px] text-pmw-label-tertiary-light dark:text-pmw-label-tertiary-dark">
            %
          </span>
        </div>
      )}
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <defs>
          <mask id={maskId}>{renderTrack('stroke-white')}</mask>
        </defs>
        <g mask={`url(#${maskId})`}>
          {renderTrack('stroke-pmw-circle-bar-track-light dark:stroke-pmw-circle-bar-track-dark')}
          <circle
            className={`${progressClassName} ${progressAnimationClassName}`}
            cx={cx}
            cy={cy}
            r={radius}
            fill="none"
            strokeWidth={strokeWidth}
            strokeDasharray={`${progressLength} ${progressGap}`}
            strokeDashoffset={progressOffset}
            transform={`rotate(-90 ${cx} ${cy})`}
            strokeLinecap="round"
          />
        </g>
      </svg>
    </div>
  );
}
